import { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../i18n';

export type TimeRange = '24h' | '7d' | '30d' | '90d' | 'custom';

export interface TimeFilterValue {
  range: TimeRange;
  days: number;
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
}

interface ChartTimeFilterProps {
  value: TimeFilterValue;
  onChange: (value: TimeFilterValue) => void;
}

const PRESETS: { key: TimeRange; labelKey: 'eq3_tf_24h' | 'eq2_tf_7d' | 'eq2_tf_30d' | 'eq2_tf_90d'; days: number }[] = [
  { key: '24h', labelKey: 'eq3_tf_24h', days: 1 },
  { key: '7d', labelKey: 'eq2_tf_7d', days: 7 },
  { key: '30d', labelKey: 'eq2_tf_30d', days: 30 },
  { key: '90d', labelKey: 'eq2_tf_90d', days: 90 },
];

function formatDateForInput(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function ChartTimeFilter({ value, onChange }: ChartTimeFilterProps) {
  const { t, locale } = useI18n();
  const [showCalendar, setShowCalendar] = useState(false);
  const [tempStart, setTempStart] = useState(value.startDate || formatDateForInput(new Date(Date.now() - 30 * 86400000)));
  const [tempEnd, setTempEnd] = useState(value.endDate || formatDateForInput(new Date()));
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setShowCalendar(false);
      }
    }
    if (showCalendar) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCalendar]);

  const handlePreset = (preset: typeof PRESETS[number]) => {
    setShowCalendar(false);
    onChange({ range: preset.key, days: preset.days });
  };

  const handleApplyCustom = () => {
    if (tempStart && tempEnd && tempStart <= tempEnd) {
      const diffDays = Math.ceil((new Date(tempEnd).getTime() - new Date(tempStart).getTime()) / 86400000) + 1;
      onChange({ range: 'custom', days: diffDays, startDate: tempStart, endDate: tempEnd });
      setShowCalendar(false);
    }
  };

  const activeLabel = value.range === 'custom' && value.startDate && value.endDate
    ? `${new Date(value.startDate).toLocaleDateString(locale, { day: '2-digit', month: 'short' })} – ${new Date(value.endDate).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })}`
    : null;

  return (
    <div className="flex items-center gap-0.5 flex-wrap relative">
      {/* Segmented control container */}
      <div className="inline-flex items-center bg-gray-100/80 rounded-lg p-0.5 border border-gray-200/60">
        {PRESETS.map(p => (
          <button
            key={p.key}
            onClick={() => handlePreset(p)}
            className={`
              relative px-3.5 py-1.5 rounded-md text-[11px] font-semibold tracking-wide uppercase transition-all duration-200
              ${value.range === p.key
                ? 'bg-white text-[#0d3b66] shadow-sm ring-1 ring-gray-200/80'
                : 'text-[#6b7280] hover:text-[#0d3b66]'
              }
            `}
          >
            {t(p.labelKey)}
          </button>
        ))}

        {/* Calendar / Custom date toggle */}
        <div className="relative" ref={calendarRef}>
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold tracking-wide uppercase transition-all duration-200
              ${value.range === 'custom'
                ? 'bg-white text-[#0d3b66] shadow-sm ring-1 ring-gray-200/80'
                : 'text-[#6b7280] hover:text-[#0d3b66]'
              }
            `}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            {activeLabel ? (
              <span className="normal-case tracking-normal max-w-[150px] truncate text-[10px]">{activeLabel}</span>
            ) : (
              <span>{t('eq2_tf_period')}</span>
            )}
          </button>

          {/* Calendar dropdown */}
          {showCalendar && (
            <div className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-2xl border border-gray-100 p-5 min-w-[290px]"
                 style={{ animation: 'fadeSlideIn 150ms ease-out' }}>
              <style>{`@keyframes fadeSlideIn { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
              
              <div className="text-xs font-semibold text-[#0d3b66] mb-4 flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-[#1e88e5]/10 flex items-center justify-center">
                  <svg className="w-3.5 h-3.5 text-[#1e88e5]" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                {t('eq2_tf_custom_range')}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-semibold text-[#6b7280] uppercase tracking-wider mb-1.5">{t('eq2_tf_start')}</label>
                  <input
                    type="date"
                    value={tempStart}
                    onChange={e => setTempStart(e.target.value)}
                    max={tempEnd}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-[#0d3b66] font-medium bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#1e88e5]/20 focus:border-[#1e88e5] focus:bg-white transition-all"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-[#6b7280] uppercase tracking-wider mb-1.5">{t('eq2_tf_end')}</label>
                  <input
                    type="date"
                    value={tempEnd}
                    onChange={e => setTempEnd(e.target.value)}
                    min={tempStart}
                    max={formatDateForInput(new Date())}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs text-[#0d3b66] font-medium bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#1e88e5]/20 focus:border-[#1e88e5] focus:bg-white transition-all"
                  />
                </div>

                {/* Quick shortcuts */}
                <div className="flex gap-1.5 pt-1">
                  {[
                    { label: t('eq2_tf_this_week'), fn: () => { const d = new Date(); const day = d.getDay() || 7; const mon = new Date(d); mon.setDate(d.getDate() - day + 1); setTempStart(formatDateForInput(mon)); setTempEnd(formatDateForInput(d)); }},
                    { label: t('eq2_tf_this_month'), fn: () => { const d = new Date(); setTempStart(formatDateForInput(new Date(d.getFullYear(), d.getMonth(), 1))); setTempEnd(formatDateForInput(d)); }},
                    { label: t('eq2_tf_last_month'), fn: () => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - 1, 1); const e = new Date(d.getFullYear(), d.getMonth(), 0); setTempStart(formatDateForInput(s)); setTempEnd(formatDateForInput(e)); }},
                  ].map(s => (
                    <button
                      key={s.label}
                      onClick={s.fn}
                      className="flex-1 px-2 py-1.5 text-[10px] font-semibold text-[#0d3b66] bg-gray-50 border border-gray-200 rounded-md hover:bg-[#1e88e5]/5 hover:border-[#1e88e5]/30 hover:text-[#1e88e5] transition-all"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                <button
                  onClick={handleApplyCustom}
                  disabled={!tempStart || !tempEnd || tempStart > tempEnd}
                  className="w-full py-2.5 bg-[#0d3b66] text-white text-xs font-semibold rounded-lg hover:bg-[#1e88e5] transition-colors disabled:opacity-30 disabled:cursor-not-allowed mt-1"
                >
                  {t('eq2_apply')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ChartTimeFilter;
